/**
 * The Asset Management responses as the platform sends them, validated, and
 * mapped to the console's names. `routers/asset_management.py` is the source.
 */
import { z } from "zod";
import type {
  AmAsset,
  AmAssetDetail,
  AmAssetRow,
  AmCondition,
  AmLife,
  AmLifecycle,
  AmMaintenance,
  AmPlan,
  AmPolicy,
  AmRegister,
  AmTypeDef,
  AmHistoryEvent,
  AmNote,
  AttrSchema,
  LifecycleStage,
  NoteCategory,
} from "../domain/assetManagement";

const num = z.number().nullable().optional();
const str = z.string().nullable().optional();

export const wireRegister = z.object({
  site: z.object({ id: z.string(), code: z.string(), name: z.string() }),
  zones: z.array(
    z.object({
      id: z.string(),
      parent_id: str,
      code: z.string(),
      name: z.string(),
      level: num,
      zone_type: z.string(),
      assets: z.number(),
    }),
  ),
  types: z.array(z.object({ key: z.string(), name: z.string(), category: str, assets: z.number() })),
  assets: z.array(
    z.object({
      id: z.string(),
      tag: z.string(),
      name: z.string(),
      asset_type: z.string(),
      asset_type_name: z.string(),
      zone_id: str,
      zone_code: str,
      parent_id: str,
      criticality: z.number(),
      status: z.string(),
      lifecycle_stage: z.string(),
      condition_score: num,
      health_score: num,
      health_band: num,
      installed_on: str,
      manufacturer: str,
      model: str,
      serial_no: str,
      children: z.number(),
    }),
  ),
  total: z.number(),
  truncated: z.boolean(),
  coverage: z.object({
    assets: z.number(),
    dated: z.number(),
    assessed: z.number(),
    with_maker: z.number(),
    components: z.number(),
  }),
});

export function mapRegister(r: z.infer<typeof wireRegister>): AmRegister {
  return {
    site: r.site,
    zones: r.zones.map((z) => ({
      id: z.id,
      parentId: z.parent_id ?? null,
      code: z.code,
      name: z.name,
      level: z.level ?? null,
      zoneType: z.zone_type,
      assets: z.assets,
    })),
    types: r.types.map((t) => ({ key: t.key, name: t.name, category: t.category ?? null, assets: t.assets })),
    assets: r.assets.map((a): AmAssetRow => ({
      id: a.id,
      tag: a.tag,
      name: a.name,
      assetType: a.asset_type,
      assetTypeName: a.asset_type_name,
      zoneId: a.zone_id ?? null,
      zoneCode: a.zone_code ?? null,
      parentId: a.parent_id ?? null,
      criticality: a.criticality,
      status: a.status,
      stage: a.lifecycle_stage as LifecycleStage,
      conditionScore: a.condition_score ?? null,
      healthScore: a.health_score ?? null,
      healthBand: a.health_band ?? null,
      installedOn: a.installed_on ?? null,
      manufacturer: a.manufacturer ?? null,
      model: a.model ?? null,
      serialNo: a.serial_no ?? null,
      children: a.children,
    })),
    total: r.total,
    truncated: r.truncated,
    coverage: {
      assets: r.coverage.assets,
      dated: r.coverage.dated,
      assessed: r.coverage.assessed,
      withMaker: r.coverage.with_maker,
      components: r.coverage.components,
    },
  };
}

const wireLife = z.object({
  design_life_years: num,
  life_source: str,
  replacement_value: num,
  value_source: str,
  age_years: num,
  remaining_years: num,
  consumed_pct: num,
  end_of_life: str,
  book_value: num,
  warranty_days_left: num,
});

function mapLife(l: z.infer<typeof wireLife>): AmLife {
  return {
    designLifeYears: l.design_life_years ?? null,
    lifeSource: (l.life_source as AmLife["lifeSource"]) ?? null,
    replacementValue: l.replacement_value ?? null,
    valueSource: (l.value_source as AmLife["valueSource"]) ?? null,
    ageYears: l.age_years ?? null,
    remainingYears: l.remaining_years ?? null,
    consumedPct: l.consumed_pct ?? null,
    endOfLife: l.end_of_life ?? null,
    bookValue: l.book_value ?? null,
    warrantyDaysLeft: l.warranty_days_left ?? null,
  };
}

const wirePolicy = z.object({
  asset_type: z.string(),
  design_life_years: num,
  replacement_cost: num,
  currency: z.string(),
  note: str,
});

export function mapPolicy(p: z.infer<typeof wirePolicy>): AmPolicy {
  return {
    assetType: p.asset_type,
    designLifeYears: p.design_life_years ?? null,
    replacementCost: p.replacement_cost ?? null,
    currency: p.currency,
    note: p.note ?? null,
  };
}
export const wirePolicies = z.object({ policies: z.array(wirePolicy) });
export { wirePolicy };

const wirePlan = z.object({
  id: z.string(),
  asset_id: str,
  tag: str,
  asset_name: str,
  asset_type: str,
  criticality: num,
  title: z.string(),
  trigger: z.enum(["calendar", "usage"]),
  interval_days: num,
  usage_measurement: str,
  interval_usage: num,
  lead_days: num,
  priority: z.number(),
  estimated_hours: num,
  estimated_cost: num,
  tasks: z.array(z.unknown()).nullable().optional(),
  active: z.boolean(),
  last_done_at: str,
  next_due_at: str,
  state: z.enum(["overdue", "due", "scheduled", "waiting", "paused"]).optional(),
  open_work_order: str,
});

export function mapPlan(p: z.infer<typeof wirePlan>): AmPlan {
  return {
    id: p.id,
    assetId: p.asset_id ?? undefined,
    tag: p.tag ?? undefined,
    assetName: p.asset_name ?? undefined,
    assetType: p.asset_type ?? undefined,
    criticality: p.criticality ?? undefined,
    title: p.title,
    trigger: p.trigger,
    intervalDays: p.interval_days ?? null,
    usageMeasurement: p.usage_measurement ?? null,
    intervalUsage: p.interval_usage ?? null,
    leadDays: p.lead_days ?? undefined,
    priority: p.priority,
    estimatedHours: p.estimated_hours ?? null,
    estimatedCost: p.estimated_cost ?? null,
    tasks: (p.tasks ?? []).map(String),
    active: p.active,
    lastDoneAt: p.last_done_at ?? null,
    nextDueAt: p.next_due_at ?? null,
    state: p.state ?? "scheduled",
    openWorkOrder: p.open_work_order ?? null,
  };
}
export { wirePlan };

export const wireDetail = z.object({
  asset: z
    .object({
      id: z.string(),
      site_id: z.string(),
      tag: z.string(),
      name: z.string(),
      asset_type: z.string(),
      asset_type_name: z.string(),
      category: str,
      condition_model: str,
      zone_code: str,
      zone_name: str,
      parent_id: str,
      parent_tag: str,
      criticality: z.number(),
      status: z.string(),
      lifecycle_stage: z.string(),
      condition_score: num,
      condition_at: str,
      condition_basis: str,
      condition_by_name: str,
      health_score: num,
      health_band: num,
      health_basis: z.record(z.string(), z.unknown()).nullable().optional(),
      installed_on: str,
      manufacturer: str,
      model: str,
      serial_no: str,
      purchased_on: str,
      purchase_cost: num,
      replacement_value: num,
      design_life_years: num,
      warranty_until: str,
      disposed_on: str,
      depreciation_method: z.string(),
      residual_pct: z.number(),
      attrs: z.record(z.string(), z.unknown()).nullable().optional(),
    })
    .passthrough(),
  zone_path: z.array(z.object({ code: z.string(), name: z.string() })),
  children: z.array(
    z.object({
      id: z.string(),
      tag: z.string(),
      name: z.string(),
      asset_type: z.string(),
      health_band: num,
      lifecycle_stage: z.string(),
    }),
  ),
  work_orders: z.array(
    z.object({
      id: z.string(),
      number: z.string(),
      kind: z.string(),
      status: z.string(),
      priority: z.number(),
      title: z.string(),
      opened_at: z.string(),
      completed_at: str,
      cost_actual: num,
    }),
  ),
  defects: z.array(
    z.object({
      id: z.string(),
      defect_type: str,
      severity: z.number(),
      status: z.string(),
      summary: str,
      detected_at: z.string(),
    }),
  ),
  readings: z.array(
    z.object({
      measurement: z.string(),
      unit: str,
      value_num: num,
      observed_at: z.string(),
      source: str,
    }),
  ),
  predictions: z.array(
    z.object({
      model_key: z.string(),
      horizon_days: num,
      predicted_value: num,
      probability: num,
      confidence: num,
      rul_p50_days: num,
      predicted_at: z.string(),
    }),
  ),
  documents: z.array(z.object({ id: z.string(), title: z.string(), relation: str })),
  plans: z.array(wirePlan),
  health_history: z.array(z.object({ observed_on: z.string(), score: z.number(), band: z.number() })),
  life: wireLife,
  policy: wirePolicy.nullable().optional(),
  reliability: z.object({ failures: z.number(), mtbf_days: num, mttr_hours: num }),
  cost_to_date: z.number(),
});

export function mapDetail(d: z.infer<typeof wireDetail>): AmAssetDetail {
  const a = d.asset;
  const asset: AmAsset = {
    id: a.id,
    siteId: a.site_id,
    tag: a.tag,
    name: a.name,
    assetType: a.asset_type,
    assetTypeName: a.asset_type_name,
    category: a.category ?? null,
    conditionModel: a.condition_model ?? null,
    zoneCode: a.zone_code ?? null,
    zoneName: a.zone_name ?? null,
    parentId: a.parent_id ?? null,
    parentTag: a.parent_tag ?? null,
    criticality: a.criticality,
    status: a.status,
    stage: a.lifecycle_stage as LifecycleStage,
    conditionScore: a.condition_score ?? null,
    conditionAt: a.condition_at ?? null,
    conditionBasis: a.condition_basis ?? null,
    conditionBy: a.condition_by_name ?? null,
    healthScore: a.health_score ?? null,
    healthBand: a.health_band ?? null,
    healthBasis: a.health_basis ?? null,
    installedOn: a.installed_on ?? null,
    manufacturer: a.manufacturer ?? null,
    model: a.model ?? null,
    serialNo: a.serial_no ?? null,
    purchasedOn: a.purchased_on ?? null,
    purchaseCost: a.purchase_cost ?? null,
    replacementValue: a.replacement_value ?? null,
    designLifeYears: a.design_life_years ?? null,
    warrantyUntil: a.warranty_until ?? null,
    disposedOn: a.disposed_on ?? null,
    depreciationMethod: a.depreciation_method as AmAsset["depreciationMethod"],
    residualPct: a.residual_pct,
    attrs: a.attrs ?? {},
  };
  return {
    asset,
    zonePath: d.zone_path,
    children: d.children.map((c) => ({
      id: c.id,
      tag: c.tag,
      name: c.name,
      assetType: c.asset_type,
      healthBand: c.health_band ?? null,
      stage: c.lifecycle_stage,
    })),
    workOrders: d.work_orders.map((w) => ({
      id: w.id,
      number: w.number,
      kind: w.kind,
      status: w.status,
      priority: w.priority,
      title: w.title,
      openedAt: w.opened_at,
      completedAt: w.completed_at ?? null,
      costActual: w.cost_actual ?? null,
    })),
    defects: d.defects.map((x) => ({
      id: x.id,
      defectType: x.defect_type ?? null,
      severity: x.severity,
      status: x.status,
      summary: x.summary ?? null,
      detectedAt: x.detected_at,
    })),
    readings: d.readings.map((r) => ({
      measurement: r.measurement,
      unit: r.unit ?? null,
      value: r.value_num ?? null,
      observedAt: r.observed_at,
      source: r.source ?? null,
    })),
    predictions: d.predictions.map((p) => ({
      modelKey: p.model_key,
      horizonDays: p.horizon_days ?? null,
      predictedValue: p.predicted_value ?? null,
      probability: p.probability ?? null,
      confidence: p.confidence ?? null,
      rulP50Days: p.rul_p50_days ?? null,
      predictedAt: p.predicted_at,
    })),
    documents: d.documents.map((x) => ({ id: x.id, title: x.title, relation: x.relation ?? null })),
    plans: d.plans.map(mapPlan),
    healthHistory: d.health_history.map((h) => ({ observedOn: h.observed_on, score: h.score, band: h.band })),
    life: mapLife(d.life),
    policy: d.policy ? mapPolicy(d.policy) : null,
    reliability: {
      failures: d.reliability.failures,
      mtbfDays: d.reliability.mtbf_days ?? null,
      mttrHours: d.reliability.mttr_hours ?? null,
    },
    costToDate: d.cost_to_date,
  };
}

const wireRisk = z.object({
  id: z.string(),
  tag: z.string(),
  name: z.string(),
  asset_type: z.string(),
  zone_code: str,
  criticality: z.number(),
  health_score: z.number(),
  health_band: z.number(),
  risk: z.number(),
  basis: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const wireCondition = z.object({
  assets: z.number(),
  assessed: z.number(),
  unassessed: z.number(),
  matrix: z.array(z.array(z.number())),
  bands: z.record(z.string(), z.number()),
  top_risks: z.array(wireRisk),
  high_risk: z.number(),
  by_type: z.array(
    z.object({
      asset_type: z.string(),
      name: z.string(),
      assets: z.number(),
      assessed: z.number(),
      mean_health: num,
      poor: z.number(),
    }),
  ),
  trend: z.array(z.object({ month: z.string(), mean_health: z.number(), assets: z.number() })),
  /* build 6B (absent from a platform older than 0093) */
  surveyed: z.number().default(0),
  recent_surveys: z
    .array(
      z.object({
        id: z.string(),
        tag: z.string(),
        name: str,
        condition_score: z.number(),
        condition_at: str,
        condition_basis: str,
        condition_by: str,
        health_score: num,
        health_band: num,
      }),
    )
    .default([]),
  method: z.string(),
});

export function mapCondition(c: z.infer<typeof wireCondition>): AmCondition {
  return {
    assets: c.assets,
    assessed: c.assessed,
    unassessed: c.unassessed,
    matrix: c.matrix,
    bands: c.bands,
    topRisks: c.top_risks.map((r) => ({
      id: r.id,
      tag: r.tag,
      name: r.name,
      assetType: r.asset_type,
      zoneCode: r.zone_code ?? null,
      criticality: r.criticality,
      healthScore: r.health_score,
      healthBand: r.health_band,
      risk: r.risk,
      basis: r.basis ?? null,
    })),
    highRisk: c.high_risk,
    byType: c.by_type.map((t) => ({
      assetType: t.asset_type,
      name: t.name,
      assets: t.assets,
      assessed: t.assessed,
      meanHealth: t.mean_health ?? null,
      poor: t.poor,
    })),
    trend: c.trend.map((t) => ({ month: t.month, meanHealth: t.mean_health, assets: t.assets })),
    surveyed: c.surveyed,
    recentSurveys: c.recent_surveys.map((r) => ({
      id: r.id,
      tag: r.tag,
      name: r.name ?? "",
      conditionScore: r.condition_score,
      conditionAt: r.condition_at ?? null,
      conditionBasis: r.condition_basis ?? null,
      conditionBy: r.condition_by ?? null,
      healthScore: r.health_score ?? null,
      healthBand: r.health_band ?? null,
    })),
    method: c.method,
  };
}

export const wireMaintenance = z.object({
  plans: z.array(wirePlan),
  summary: z.object({
    plans: z.number(),
    active: z.number(),
    overdue: z.number(),
    due: z.number(),
    due_30_days: z.number(),
    compliance_pct: num,
    pm_completed_12m: z.number(),
    pm_on_time_12m: z.number(),
    planned_share_pct: num,
  }),
  work_mix_12m: z.record(z.string(), z.object({ count: z.number(), cost: z.number() })),
  bad_actors: z.array(
    z.object({
      id: z.string(),
      tag: z.string(),
      name: z.string(),
      asset_type: z.string(),
      criticality: z.number(),
      failures: z.number(),
      mtbf_days: num,
      mttr_hours: num,
      cost: z.number(),
    }),
  ),
  coverage: z.object({ work_orders_24m: z.number(), linked_to_asset: z.number() }),
  method: z.string(),
});

export function mapMaintenance(m: z.infer<typeof wireMaintenance>): AmMaintenance {
  const s = m.summary;
  return {
    plans: m.plans.map(mapPlan),
    summary: {
      plans: s.plans,
      active: s.active,
      overdue: s.overdue,
      due: s.due,
      due30Days: s.due_30_days,
      compliancePct: s.compliance_pct ?? null,
      pmCompleted12m: s.pm_completed_12m,
      pmOnTime12m: s.pm_on_time_12m,
      plannedSharePct: s.planned_share_pct ?? null,
    },
    workMix12m: m.work_mix_12m,
    badActors: m.bad_actors.map((b) => ({
      id: b.id,
      tag: b.tag,
      name: b.name,
      assetType: b.asset_type,
      criticality: b.criticality,
      failures: b.failures,
      mtbfDays: b.mtbf_days ?? null,
      mttrHours: b.mttr_hours ?? null,
      cost: b.cost,
    })),
    coverage: { workOrders24m: m.coverage.work_orders_24m, linkedToAsset: m.coverage.linked_to_asset },
    method: m.method,
  };
}

export const wireLifecycle = z.object({
  coverage: z.object({
    assets: z.number(),
    dated: z.number(),
    life_known: z.number(),
    life_from_policy: z.number(),
    valued: z.number(),
    value_from_policy: z.number(),
    forecastable: z.number(),
  }),
  totals: z.object({
    replacement_value: z.number(),
    book_value: z.number(),
    valued_assets: z.number(),
    booked_assets: z.number(),
    past_end_of_life: z.number(),
  }),
  stages: z.record(z.string(), z.number()),
  forecast: z.array(
    z.object({
      year: z.number(),
      count: z.number(),
      cost: z.number(),
      critical: z.number(),
      uncosted: z.number(),
      funded: num,
    }),
  ),
  warranties: z.object({
    expiring_90: z.array(
      z.object({
        id: z.string(),
        tag: z.string(),
        name: z.string(),
        warranty_until: z.string(),
        days_left: z.number(),
      }),
    ),
    expired: z.number(),
    in_warranty: z.number(),
  }),
  assets: z.array(
    wireLife.extend({
      id: z.string(),
      tag: z.string(),
      name: z.string(),
      asset_type: z.string(),
      asset_type_name: z.string(),
      criticality: z.number(),
      stage: z.string(),
      health_band: num,
    }),
  ),
  policies: z.array(wirePolicy),
  method: z.string(),
});

export function mapLifecycle(l: z.infer<typeof wireLifecycle>): AmLifecycle {
  return {
    coverage: {
      assets: l.coverage.assets,
      dated: l.coverage.dated,
      lifeKnown: l.coverage.life_known,
      lifeFromPolicy: l.coverage.life_from_policy,
      valued: l.coverage.valued,
      valueFromPolicy: l.coverage.value_from_policy,
      forecastable: l.coverage.forecastable,
    },
    totals: {
      replacementValue: l.totals.replacement_value,
      bookValue: l.totals.book_value,
      valuedAssets: l.totals.valued_assets,
      bookedAssets: l.totals.booked_assets,
      pastEndOfLife: l.totals.past_end_of_life,
    },
    stages: l.stages,
    forecast: l.forecast.map((f) => ({ ...f, funded: f.funded ?? null })),
    warranties: {
      expiring90: l.warranties.expiring_90.map((w) => ({
        id: w.id,
        tag: w.tag,
        name: w.name,
        warrantyUntil: w.warranty_until,
        daysLeft: w.days_left,
      })),
      expired: l.warranties.expired,
      inWarranty: l.warranties.in_warranty,
    },
    assets: l.assets.map((a) => ({
      ...mapLife(a),
      id: a.id,
      tag: a.tag,
      name: a.name,
      assetType: a.asset_type,
      assetTypeName: a.asset_type_name,
      criticality: a.criticality,
      stage: a.stage as LifecycleStage,
      healthBand: a.health_band ?? null,
    })),
    policies: l.policies.map(mapPolicy),
    method: l.method,
  };
}

export const wireTypes = z.object({
  types: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      category: str,
      geometry_kind: str,
      condition_model: str,
      schema: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
  ),
});

export function mapTypes(r: z.infer<typeof wireTypes>): AmTypeDef[] {
  return r.types.map((t) => ({
    key: t.key,
    name: t.name,
    category: t.category ?? null,
    geometryKind: t.geometry_kind ?? null,
    conditionModel: t.condition_model ?? null,
    schema: (t.schema ?? {}) as AttrSchema,
  }));
}

export const wireHistory = z.object({
  events: z.array(
    z.object({
      at: z.string(),
      kind: z.enum(["created", "changed", "work_order", "work_done", "defect", "defect_resolved", "note"]),
      who: str,
      title: z.string(),
      changes: z.array(z.object({ field: z.string(), before: z.unknown(), after: z.unknown() })).optional(),
      detail: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});

export function mapHistory(r: z.infer<typeof wireHistory>): AmHistoryEvent[] {
  return r.events.map((e) => ({
    at: e.at,
    kind: e.kind,
    who: e.who ?? null,
    title: e.title,
    changes: e.changes?.map((c) => ({ field: c.field, before: c.before, after: c.after })),
    detail: e.detail,
  }));
}

export const wireNotes = z.object({
  notes: z.array(
    z.object({
      id: z.string(),
      category: z.string(),
      body: z.string(),
      created_at: z.string(),
      created_by_name: str,
    }),
  ),
});

export function mapNotes(r: z.infer<typeof wireNotes>): AmNote[] {
  return r.notes.map((n) => ({
    id: n.id,
    category: n.category as NoteCategory,
    body: n.body,
    createdAt: n.created_at,
    createdBy: n.created_by_name ?? null,
  }));
}
